/**
 * EmailDraftHelp — collapsible "How this works" panel above the Email Drafter
 * form (X.3.4.6 → X.6.4B reskin).
 *
 * Cream card, gold left-accent, forest heading; collapsible (default collapsed (founder ruling 2026-07-06)).
 * X.6.4B migrated the locked inline hexes to x.6.1 tokens and swapped the
 * +/- glyph + bullet emoji for Lucide icons. Copy, behavior, and test ids are
 * unchanged. LP-31: the fields' placeholders and the buttons explain
 * themselves, so the panel carries Amanda's one line on the content box and
 * the drafts note.
 */

import { type ReactNode, useState } from 'react';
import { ChevronDown, ChevronRight, Star } from 'lucide-react';

export function EmailDraftHelp(): ReactNode {
  // Founder ruling 2026-07-06: collapsed by default — the panel is
  // reference, not a gate; the workspace leads.
  const [open, setOpen] = useState(false);

  return (
    <section
      className="overflow-hidden rounded-card border border-warm-400/25 border-l-[3px] border-l-warm-400 bg-cream"
      data-testid="email-draft-help"
      aria-label="How this works"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-[18px] py-[13px] text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        aria-expanded={open}
        aria-controls="email-draft-help-body"
        data-testid="email-draft-help-toggle"
      >
        <span className="font-display text-[15px] font-semibold">How this works</span>
        {open ? (
          <ChevronDown size={18} aria-hidden="true" className="text-sage-400" />
        ) : (
          <ChevronRight size={18} aria-hidden="true" className="text-sage-400" />
        )}
      </button>

      {open && (
        <div
          id="email-draft-help-body"
          className="space-y-2 px-[18px] pb-4 text-[13px] leading-relaxed text-ink"
          data-testid="email-draft-help-body"
        >
          <p>Say what you would like to say; rough notes are fine.</p>
          <p className="flex items-start gap-2 pt-1 text-sage-600">
            <Star size={14} aria-hidden="true" className="mt-0.5 shrink-0 text-warm-400" />
            <span>
              <span className="font-semibold">Note:</span>
              {' '}You can save up to 10 drafts at a time (found on the Writing page under &lsquo;Drafts&rsquo;).
            </span>
          </p>
        </div>
      )}
    </section>
  );
}
